'use client'

import React, { useEffect, useMemo, useState } from "react"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "./ui/form"
import { Input } from "./ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Textarea } from "./ui/textarea"
import { Badge } from "./ui/badge"
import { 
  DollarSign, 
  Building, 
  FileText, 
  Calculator, 
  Plus, 
  CheckCircle,
  Calendar,
  CreditCard,
  Tag,
  Trash2
} from "lucide-react"
import { Button } from "./ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table"
import { VendorDropdown } from "./vendor-dropdown"
import { cn } from "../lib/utils"

interface ExpenseFormFieldsProps {
  form: any
  companyName: string
  filteredCategories: any[]
  filteredBudgets: any[]
  vendors: any[]
}

export function ExpenseFormFields({ 
  form, 
  companyName, 
  filteredCategories, 
  filteredBudgets, 
  vendors 
}: ExpenseFormFieldsProps) {
  return (
    <div className="space-y-4 py-4 px-4 pb-3 bg-gradient-to-br from-slate-50 to-white">
      {/* Expense Information Section */}
      <div className="border border-slate-200 rounded-lg p-4 bg-white shadow-sm">
          <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <div className="p-1.5 bg-teal-50 rounded-lg">
              <FileText className="w-4 h-4 text-teal-600" />
            </div>
            Expense Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="companyId"
                render={({ field }: any) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-slate-700">Company *</FormLabel>
                    <FormControl>
                      <Input 
                        value={companyName} 
                        disabled 
                        className="h-9 bg-slate-50 border-slate-300 text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="expenseDate"
                render={({ field }: any) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-slate-700">Expense Date *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input type="date" className="h-9 pr-9 text-sm border-slate-300 focus:border-teal-500 focus:ring-teal-500 transition-colors" {...field} />
                        <Calendar className="absolute right-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="referenceNumber"
                render={({ field }: any) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-slate-700">Reference/Invoice Number</FormLabel>
                      <FormControl>
                        <Input 
                        placeholder="INV-12345" 
                        className="h-9 text-sm border-slate-300 focus:border-teal-500 focus:ring-teal-500 transition-colors" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }: any) => (
                    <FormItem className="w-full">
                    <FormLabel className="text-xs font-medium text-slate-700">Currency</FormLabel>
                    <Select value={field.value || 'Frw'} onValueChange={field.onChange}>
                        <FormControl>
                        <SelectTrigger className="h-9 w-full text-sm border-slate-300 focus:border-teal-500 focus:ring-teal-500 transition-colors">
                            <SelectValue placeholder="Frw" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                        <SelectItem value="Frw">Frw (RWF)</SelectItem>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="GBP">GBP</SelectItem>
                          <SelectItem value="CAD">CAD</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              </div>
              
      {/* Line Items Section */}
      <div className="border border-slate-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
                <FormField
                  control={form.control}
              name="lineItems"
              render={({ field }: any) => {
                const lineItems = field.value || []
                
                // Memoize calculations to prevent unnecessary recalculations
                const { subtotal, totalTax, grandTotal } = useMemo(() => {
                  const sub = lineItems.reduce((sum: number, item: any) => {
                    const qty = Number(item.quantity || 1)
                    const price = Number(item.unitPrice || 0)
                    return sum + (qty * price)
                  }, 0)
                  
                  const tax = lineItems.reduce((sum: number, item: any) => {
                    const qty = Number(item.quantity || 1)
                    const price = Number(item.unitPrice || 0)
                    const taxRate = Number(item.taxRate || 0)
                    return sum + (qty * price * (taxRate / 100))
                  }, 0)
                  
                  return {
                    subtotal: sub,
                    totalTax: tax,
                    grandTotal: sub + tax
                  }
                }, [lineItems.map((item: any) => `${item.quantity}-${item.unitPrice}-${item.taxRate}`).join(',')])
                
                // Update form values when line items change (using useEffect to avoid infinite loop)
                useEffect(() => {
                  if (lineItems.length > 0) {
                    form.setValue('amount', subtotal, { shouldValidate: false, shouldDirty: false })
                    form.setValue('taxAmount', totalTax, { shouldValidate: false, shouldDirty: false })
                    form.setValue('totalAmount', grandTotal, { shouldValidate: false, shouldDirty: false })
                  } else {
                    // Reset to 0 if no line items
                    if (form.getValues('amount') === subtotal && subtotal === 0) {
                      // Only reset if amount was calculated from line items
                      form.setValue('amount', 0, { shouldValidate: false, shouldDirty: false })
                      form.setValue('taxAmount', 0, { shouldValidate: false, shouldDirty: false })
                      form.setValue('totalAmount', 0, { shouldValidate: false, shouldDirty: false })
                    }
                  }
                  // eslint-disable-next-line react-hooks/exhaustive-deps
                }, [subtotal, totalTax, grandTotal, lineItems.length])
                
                const lineItemsErrors = form.formState.errors.lineItems
                
                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        <div className="p-1.5 bg-blue-50 rounded-lg">
                          <FileText className="w-3.5 h-3.5 text-blue-600" />
                        </div>
                        Line Items
                        {lineItems.length > 0 && (
                          <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200 text-xs px-1.5 py-0.5">
                            {lineItems.length} {lineItems.length === 1 ? 'item' : 'items'}
                          </Badge>
                        )}
                      </h3>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newItem = {
                            itemCode: undefined,
                            categoryId: undefined,
                            budgetId: undefined,
                            description: '',
                            vendorId: undefined,
                            vendorName: undefined,
                            quantity: 1,
                            taxRate: 18,
                            taxType: 'B',
                            unitPrice: 0
                          }
                          field.onChange([...lineItems, newItem])
                        }}
                        className="bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow transition-all text-xs h-8 px-3"
                      >
                        <Plus className="w-3 h-3 mr-1.5" />
                        Add Line Item
                      </Button>
          </div>

                    {lineItems.length === 0 ? (
                      <div className="space-y-2">
                        <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg bg-slate-50">
                          <FileText className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                          <p className="text-slate-500 text-xs font-medium mb-1">No line items added yet</p>
                          <p className="text-slate-400 text-xs">Click "Add Line Item" to start adding items to this expense</p>
                        </div>
                        {lineItemsErrors && (
                          <p className="text-xs text-red-600 text-center">{lineItemsErrors.message || 'At least one line item is required'}</p>
                        )}
                      </div>
                    ) : (
                      <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gradient-to-r from-slate-50 to-slate-100">
                                <TableHead className="w-[100px] font-semibold text-slate-700 text-xs py-2">Item Code</TableHead>
                                <TableHead className="w-[150px] font-semibold text-slate-700 text-xs py-2">Category</TableHead>
                                <TableHead className="w-[150px] font-semibold text-slate-700 text-xs py-2">Budget</TableHead>
                                <TableHead className="font-semibold text-slate-700 text-xs py-2">Description</TableHead>
                                <TableHead className="w-[120px] font-semibold text-slate-700 text-xs py-2">Vendor</TableHead>
                                <TableHead className="w-[70px] font-semibold text-slate-700 text-xs py-2">Qty</TableHead>
                                <TableHead className="w-[90px] font-semibold text-slate-700 text-xs py-2">Tax %</TableHead>
                                <TableHead className="w-[110px] font-semibold text-slate-700 text-xs py-2">Unit Price</TableHead>
                                <TableHead className="w-[110px] font-semibold text-slate-700 text-xs py-2">Total</TableHead>
                                <TableHead className="w-[50px] text-xs py-2"></TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {lineItems.map((item: any, index: number) => {
                                const qty = Number(item.quantity || 1)
                                const price = Number(item.unitPrice || 0)
                                const taxRate = Number(item.taxRate || 0)
                                const totalPrice = qty * price
                                const taxAmount = totalPrice * (taxRate / 100)
                                const lineTotal = totalPrice + taxAmount
                                
                                // Filter budgets for the selected category
                                const categoryBudgets = item.categoryId 
                                  ? filteredBudgets.filter((budget: any) => budget.categoryId === item.categoryId && budget.isActive)
                                  : []
                                
                                return (
                                  <TableRow key={index} className="hover:bg-slate-50 transition-colors">
                                    <TableCell className="py-2">
                                      <Input
                                        placeholder="Code"
                                        value={item.itemCode || ''}
                                        onChange={(e) => {
                                          const updated = [...lineItems]
                                          updated[index] = { ...updated[index], itemCode: e.target.value }
                                          field.onChange(updated)
                                        }}
                                        className="h-9 text-sm border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                                      />
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <Select
                                        value={item.categoryId || 'none'}
                                        onValueChange={(value) => {
                                          const updated = [...lineItems]
                                          updated[index] = { 
                                            ...updated[index], 
                                            categoryId: value === 'none' ? undefined : value,
                                            budgetId: undefined // Clear budget when category changes
                                          }
                                          field.onChange(updated)
                                        }}
                                      >
                                        <SelectTrigger className="h-9 text-sm border-slate-200 focus:border-teal-500 focus:ring-teal-500">
                                          <SelectValue placeholder="Category" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="none">None</SelectItem>
                                          {filteredCategories.map((category: any) => (
                                            <SelectItem key={category.id} value={category.id}>
                                              {category.name}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      {item.categoryId && categoryBudgets.length > 0 ? (
                                        <Select
                                          value={item.budgetId || 'none'}
                                          onValueChange={(value) => {
                                            const updated = [...lineItems]
                                            updated[index] = { ...updated[index], budgetId: value === 'none' ? undefined : value }
                                            field.onChange(updated)
                                          }}
                                        >
                                          <SelectTrigger className="h-9 text-sm border-slate-200 focus:border-teal-500 focus:ring-teal-500">
                                            <SelectValue placeholder="Select budget" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">None</SelectItem>
                                            {categoryBudgets.map((budget: any) => {
                                              const amount = typeof budget.amount === 'object' ? Number(budget.amount) : budget.amount
                                              const spentAmount = typeof budget.spentAmount === 'object' ? Number(budget.spentAmount) : budget.spentAmount
                                              const available = amount - spentAmount
                                              return (
                                                <SelectItem key={budget.id} value={budget.id}>
                                                  <div className="flex flex-col">
                                                    <span className="font-medium">{budget.name}</span>
                                                    <span className="text-xs text-slate-500">
                                                      {available >= 0 ? `$${available.toLocaleString()} available` : 'Over budget'}
                                                    </span>
                                                  </div>
                                                </SelectItem>
                                              )
                                            })}
                                          </SelectContent>
                                        </Select>
                                      ) : item.categoryId ? (
                                        <div className="text-xs text-slate-400 py-2 px-2">No budgets</div>
                                      ) : (
                                        <div className="text-xs text-slate-400 py-2 px-2">Select category</div>
                                      )}
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <div className="space-y-1">
                                        <Input
                                          placeholder="Item description *"
                                          value={item.description || ''}
                                          onChange={(e) => {
                                            const updated = [...lineItems]
                                            updated[index] = { ...updated[index], description: e.target.value }
                                            field.onChange(updated)
                                          }}
                                          className={`h-9 text-sm border-slate-200 focus:border-teal-500 focus:ring-teal-500 ${
                                            lineItemsErrors?.[index]?.description ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                                          }`}
                                        />
                                        {lineItemsErrors?.[index]?.description && (
                                          <p className="text-xs text-red-600">{lineItemsErrors[index].description?.message}</p>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <VendorDropdown
                                        vendors={vendors || []}
                                        value={item.vendorId}
                                        onChange={(vendorId, vendorName) => {
                                          const updated = [...lineItems]
                                          updated[index] = {
                                            ...updated[index],
                                            vendorId,
                                            vendorName
                                          }
                                          field.onChange(updated)
                                        }}
                                        placeholder="Select vendor"
                                      />
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <div className="space-y-1">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          placeholder="1"
                                          value={item.quantity || 1}
                                          onChange={(e) => {
                                            const updated = [...lineItems]
                                            updated[index] = { ...updated[index], quantity: Number(e.target.value) || 1 }
                                            field.onChange(updated)
                                          }}
                                          className={`h-9 text-sm w-full border-slate-200 focus:border-teal-500 focus:ring-teal-500 ${
                                            lineItemsErrors?.[index]?.quantity ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                                          }`}
                                        />
                                        {lineItemsErrors?.[index]?.quantity && (
                                          <p className="text-xs text-red-600">{lineItemsErrors[index].quantity?.message}</p>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <div className="relative">
                        <Input 
                          type="number" 
                          step="0.01" 
                                          min="0"
                                          max="100"
                                          placeholder="18"
                                          value={item.taxRate || 0}
                          onChange={(e) => {
                                            const updated = [...lineItems]
                                            const taxRate = Number(e.target.value) || 0
                                            updated[index] = { 
                                              ...updated[index], 
                                              taxRate: taxRate,
                                              taxType: taxRate > 0 ? 'B' : 'A'
                                            }
                                            field.onChange(updated)
                                          }}
                                          className="h-9 text-sm w-full border-slate-200 focus:border-teal-500 focus:ring-teal-500 pr-6"
                                        />
                                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <div className="space-y-1">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          placeholder="0.00"
                                          value={item.unitPrice || 0}
                                          onChange={(e) => {
                                            const updated = [...lineItems]
                                            updated[index] = { ...updated[index], unitPrice: Number(e.target.value) || 0 }
                                            field.onChange(updated)
                                          }}
                                          className={`h-9 text-sm border-slate-200 focus:border-teal-500 focus:ring-teal-500 ${
                                            lineItemsErrors?.[index]?.unitPrice ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''
                                          }`}
                                        />
                                        {lineItemsErrors?.[index]?.unitPrice && (
                                          <p className="text-xs text-red-600">{lineItemsErrors[index].unitPrice?.message}</p>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <div className="text-xs font-semibold text-slate-900">
                                        {lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const updated = lineItems.filter((_: any, i: number) => i !== index)
                                          field.onChange(updated)
                                        }}
                                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors"
                                        title="Remove line item"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
            </div>
                        {lineItemsErrors && typeof lineItemsErrors === 'object' && !Array.isArray(lineItemsErrors) && lineItemsErrors.message && (
                          <p className="text-xs text-red-600 mt-2 px-2">{lineItemsErrors.message}</p>
                        )}
                    </div>
              )}
            </div>
                )
              }}
            />
      </div>
    </div>
  )
}

// Expense Summary Component - Clean and Simple
interface ExpenseSummaryProps {
  form: any
}

export function ExpenseSummary({ form }: ExpenseSummaryProps) {
  const baseAmount = Number(form.watch('amount')) || 0
  const taxAmount = Number(form.watch('taxAmount')) || 0
  const totalAmount = baseAmount + taxAmount
  const currency = form.watch('currency') || 'Frw'
  
  return (
    <div className="w-full">
      <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Calculator className="w-3.5 h-3.5 text-slate-600" />
            Expense Summary
          </h3>
          <Badge variant="outline" className="text-xs px-2 py-0.5">
            Preview
          </Badge>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between py-1">
            <span className="text-xs text-slate-600">Base Amount</span>
            <span className="text-sm font-medium text-slate-900">
              {baseAmount.toFixed(2)} {currency}
            </span>
          </div>
          
          {taxAmount > 0 && (
            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-slate-600 flex items-center gap-2">
                Tax
                {form.watch('taxRate') && (
                  <Badge variant="outline" className="text-xs px-1.5 py-0.5">
                    {Number(form.watch('taxRate')).toFixed(2)}%
                  </Badge>
                )}
              </span>
              <span className="text-sm font-medium text-slate-700">
                +{taxAmount.toFixed(2)} {currency}
              </span>
            </div>
          )}
          
          {(form.watch('mileage') && form.watch('mileageRate')) && (
            <div className="flex items-center justify-between py-1">
              <span className="text-xs text-slate-600">
                Mileage ({Number(form.watch('mileage')).toFixed(1)} × {Number(form.watch('mileageRate')).toFixed(2)})
              </span>
              <span className="text-sm font-medium text-slate-700">
                {((Number(form.watch('mileage')) || 0) * (Number(form.watch('mileageRate')) || 0)).toFixed(2)} {currency}
              </span>
            </div>
          )}
          
          <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-200">
            <span className="text-sm font-semibold text-slate-900">Total Expense</span>
            <div className="text-right">
              <div className="text-lg font-bold text-slate-900">
                {totalAmount.toFixed(2)} {currency}
              </div>
              {form.watch('isBillable') && (
                <Badge variant="outline" className="mt-0.5 text-xs px-1.5 py-0.5">
                  <CheckCircle className="w-2.5 h-2.5 mr-1" />
                  Billable
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
